import React, { useMemo } from 'react';
import { Box, Typography, Stack, ButtonBase, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

import { DASH, EVENT_TONES } from '../dashboard/dashboardTokens';

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

export const STATUS_TONE = '#7C3AED';

const FULL_CELL = 74;
const COMPACT_CELL = 22;

const dayKind = (day) => {
    if (!day) return 'none';
    if (day.openShift) return 'open';
    if (day.netMinutes > 0) return 'worked';
    if (day.autoEnded) return 'auto';
    if (day.statusName) return 'status';
    return 'none';
};

const formatHours = (minutes) => {
    const total = Math.round(minutes || 0);
    if (total <= 0) return null;
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) return `${m} m`;
    return m === 0 ? `${h} h` : `${h} h ${m} m`;
};

// Sýtosť výplne nesie počet hodín, rohová značka typ dňa — dve informácie,
// dva kanály. Keby ich niesla jedna farba, nedalo by sa prečítať ani jedno.
const fillAlpha = (ratio) => {
    if (ratio <= 0) return 0.10;
    if (ratio < 0.4) return 0.20;
    if (ratio < 0.65) return 0.38;
    if (ratio < 0.85) return 0.60;
    return 0.85;
};

const buildWeeks = (monthStart, byDate, maxMinutes, todayStr) => {
    const monthEnd = monthStart.endOf('month');
    const cells = [];
    // Prázdne miesta pred prvým dňom, nech pondelky sedia v stĺpci.
    const leading = (monthStart.day() + 6) % 7;
    for (let i = 0; i < leading; i += 1) cells.push(null);

    for (let cursor = monthStart; !cursor.isAfter(monthEnd, 'day'); cursor = cursor.add(1, 'day')) {
        const date = cursor.format('YYYY-MM-DD');
        const day = byDate.get(date) || null;
        cells.push({
            date,
            day,
            kind: dayKind(day),
            weekend: (cursor.day() + 6) % 7 >= 5,
            isToday: date === todayStr,
            isFuture: date > todayStr,
            dayNumber: cursor.date(),
            ratio: day ? Math.min((day.netMinutes || 0) / maxMinutes, 1) : 0,
        });
    }
    while (cells.length % 7 !== 0) cells.push(null);

    return cells;
};

/**
 * Jeden mesiac ako kalendár. Stĺpce sú dni v týždni, takže víkendy, chýbajúce
 * piatky aj celé prázdne týždne vidno bez vysvetľovania.
 */
const MonthBlock = ({
    monthStart, byDate, maxMinutes, todayStr, compact,
    weekdayLabels, locale, selectedDate, onSelect, t, title,
}) => {
    const cells = useMemo(
        () => buildWeeks(monthStart, byDate, maxMinutes, todayStr),
        [monthStart, byDate, maxMinutes, todayStr]
    );
    const height = compact ? COMPACT_CELL : FULL_CELL;

    return (
        <Box>
            {title && (
                <Typography
                    sx={{
                        fontFamily: DASH.display,
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        mb: 0.75,
                        textTransform: 'capitalize',
                    }}
                >
                    {title}
                </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: compact ? '2px' : '4px', mb: 0.5 }}>
                {weekdayLabels.map((label, idx) => (
                    <Typography
                        key={label + idx}
                        variant="caption"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: compact ? '0.5625rem' : '0.75rem',
                            color: idx >= 5 ? 'text.disabled' : 'text.secondary',
                        }}
                    >
                        {compact ? label.charAt(0) : label}
                    </Typography>
                ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: compact ? '2px' : '4px' }}>
                {cells.map((cell, idx) => {
                    if (!cell) return <Box key={`pad-${idx}`} sx={{ height }} />;

                    const selected = cell.date === selectedDate;
                    const hours = formatHours(cell.day?.netMinutes);
                    const marker = cell.kind === 'auto'
                        ? EVENT_TONES.break_start
                        : cell.kind === 'open' ? EVENT_TONES.arrival : null;

                    const tooltip = [
                        dayjs(cell.date, 'YYYY-MM-DD').locale(locale).format('dddd D. MMMM'),
                        cell.day?.firstIn ? `${cell.day.firstIn} → ${cell.day.lastOut || '…'}` : null,
                        hours,
                        cell.day?.statusName,
                        cell.kind === 'auto' ? t('pages.employeeProfile.strip.auto') : null,
                        cell.kind === 'open' ? t('pages.employeeProfile.strip.open') : null,
                    ].filter(Boolean).join('  ·  ');

                    return (
                        <Tooltip key={cell.date} title={tooltip} arrow disableInteractive>
                            <ButtonBase
                                onClick={() => onSelect(cell.date)}
                                aria-label={tooltip}
                                sx={(theme) => {
                                    const filled = cell.kind === 'worked' || cell.kind === 'auto' || cell.kind === 'open';
                                    return {
                                        height,
                                        width: '100%',
                                        borderRadius: compact ? '4px' : `${DASH.radiusXs}px`,
                                        p: compact ? 0 : 0.75,
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        zIndex: selected ? 1 : 0,
                                        transition: 'transform .12s ease',
                                        bgcolor: filled
                                            ? `rgba(37, 99, 235, ${fillAlpha(cell.ratio)})`
                                            : cell.kind === 'status'
                                                ? `${STATUS_TONE}26`
                                                : cell.weekend || cell.isFuture
                                                    ? 'transparent'
                                                    : theme.palette.action.hover,
                                        border: cell.isToday ? '1.5px solid' : '1px solid',
                                        borderColor: cell.isToday
                                            ? theme.palette.text.primary
                                            : cell.weekend || cell.isFuture ? 'transparent' : 'divider',
                                        // Výber je prstenec cez dve farby: svetlá medzera od bunky
                                        // a tmavý krúžok okolo nej. Jednofarebný rámik zmizol na
                                        // sýto modrých dňoch — modrá na modrej nie je vidieť.
                                        boxShadow: selected
                                            ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.text.primary}`
                                            : 'none',
                                        '&:hover': { transform: 'translateY(-1px)' },
                                        '&:focus-visible': {
                                            outline: '2px solid',
                                            outlineColor: theme.palette.text.primary,
                                            outlineOffset: 2,
                                        },
                                    };
                                }}
                            >
                                {compact ? null : (
                                    <>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: '100%' }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.6875rem',
                                                    fontWeight: 500,
                                                    color: cell.ratio > 0.7 ? '#fff' : 'text.secondary',
                                                    lineHeight: 1.2,
                                                    ...DASH.numeric,
                                                }}
                                            >
                                                {cell.dayNumber}
                                            </Typography>
                                            {marker && (
                                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: marker, flexShrink: 0, mt: '2px' }} />
                                            )}
                                        </Stack>

                                        <Box sx={{ mt: 'auto', width: '100%', minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: DASH.display,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1.2,
                                                    color: cell.ratio > 0.7 ? '#fff' : 'text.primary',
                                                    ...DASH.numeric,
                                                }}
                                                noWrap
                                            >
                                                {hours || (cell.day?.statusName
                                                    ? cell.day.statusName
                                                    : cell.weekend || cell.isFuture ? '' : '–')}
                                            </Typography>
                                            {cell.day?.firstIn && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.6875rem',
                                                        lineHeight: 1.3,
                                                        color: cell.ratio > 0.7 ? 'rgba(255,255,255,0.75)' : 'text.secondary',
                                                        ...DASH.numeric,
                                                    }}
                                                    noWrap
                                                >
                                                    {`${cell.day.firstIn} → ${cell.day.lastOut || '…'}`}
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                )}
                                {compact && marker && (
                                    <Box sx={{
                                        position: 'absolute', top: 2, right: 2,
                                        width: 5, height: 5, borderRadius: '50%', bgcolor: marker,
                                    }} />
                                )}
                            </ButtonBase>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

/**
 * Dochádzka ako kalendár. Mesiac dostane plné bunky s hodinami a časmi, rok
 * dvanásť malých mesiacov vedľa seba — v oboch prípadoch to ostane kalendár,
 * na ktorý netreba návod.
 */
const AttendanceCalendar = ({ days, from, to, selectedDate, onSelect, language, t }) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const todayStr = dayjs().format('YYYY-MM-DD');

    // Názvy dní berieme z locale dayjs, nie z prekladov — inak by sa rozišli.
    const weekdayLabels = useMemo(() => {
        const monday = dayjs('2024-01-01');
        return Array.from({ length: 7 }, (_, i) => {
            const label = monday.add(i, 'day').locale(locale).format('dd');
            return label.charAt(0).toUpperCase() + label.slice(1);
        });
    }, [locale]);

    const { months, byDate, maxMinutes } = useMemo(() => {
        const map = new Map((days || []).map((d) => [d.date, d]));
        const start = dayjs(from, 'YYYY-MM-DD').startOf('month');
        const end = dayjs(to, 'YYYY-MM-DD').startOf('month');
        const list = [];
        for (let cursor = start; !cursor.isAfter(end, 'month'); cursor = cursor.add(1, 'month')) {
            list.push(cursor);
        }
        return {
            months: list,
            byDate: map,
            maxMinutes: Math.max(...(days || []).map((d) => d.netMinutes || 0), 1),
        };
    }, [days, from, to]);

    const compact = months.length > 1;

    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: compact
                        ? { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }
                        : '1fr',
                    gap: compact ? 2.5 : 0,
                }}
            >
                {months.map((monthStart) => (
                    <MonthBlock
                        key={monthStart.format('YYYY-MM')}
                        monthStart={monthStart}
                        byDate={byDate}
                        maxMinutes={maxMinutes}
                        todayStr={todayStr}
                        compact={compact}
                        weekdayLabels={weekdayLabels}
                        locale={locale}
                        selectedDate={selectedDate}
                        onSelect={onSelect}
                        t={t}
                        title={compact ? monthStart.locale(locale).format('MMMM') : null}
                    />
                ))}
            </Box>

            <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 2 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.calendar.less')}
                    </Typography>
                    {[0.15, 0.5, 0.7, 1].map((ratio) => (
                        <Box
                            key={ratio}
                            sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: `rgba(37, 99, 235, ${fillAlpha(ratio)})` }}
                        />
                    ))}
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.calendar.more')}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: EVENT_TONES.break_start }} />
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.strip.auto')}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: EVENT_TONES.arrival }} />
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.strip.open')}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: `${STATUS_TONE}26`, border: '1px solid', borderColor: 'divider' }} />
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.strip.status')}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={(theme) => ({ width: 10, height: 10, borderRadius: '3px', bgcolor: theme.palette.action.hover, border: '1px solid', borderColor: 'divider' })} />
                    <Typography variant="caption" color="text.secondary">
                        {t('pages.employeeProfile.strip.missing')}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
};

export default AttendanceCalendar;
